// Vista de inicio — Tanda 5 (con inventory-store global).
//
// Esta vista NO dispara fetch directo de Graph: se suscribe al
// inventory-store que es singleton y se inicializa una sola vez por
// sesión. Esto resuelve la cascada de re-escaneos al navegar entre
// rutas y el cuelgue del Promise.all que combinaba inventario y
// validación en la misma promesa.

import { html } from 'htm/preact'
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks'

import { PROJECT_ROOT } from '../graph/sharepoint-reader.js'
import {
  initInventory,
  getInventoryState,
  subscribeInventoryChange
} from '../lib/inventory-store.js'
import {
  initValidation,
  getDocumentEntry,
  subscribeValidation
} from '../lib/validation-store.js'

import { toCssKey, toLabel } from './StatusBadge.js'
import { DocumentDetail } from './DocumentDetail.js'

// ---------------------------------------------------------------------------
// Construcción del árbol
// ---------------------------------------------------------------------------

function ensureNode(root, pathSegments) {
  let node = root
  let currentPath = ''
  for (const seg of pathSegments) {
    currentPath = currentPath ? `${currentPath}/${seg}` : seg
    if (!node.folders.has(seg)) {
      node.folders.set(seg, {
        name: seg,
        path: currentPath,
        folders: new Map(),
        files: [],
        fileCount: 0
      })
    }
    node = node.folders.get(seg)
  }
  return node
}

function buildTree(folders, files) {
  const root = {
    name: PROJECT_ROOT,
    path: '',
    folders: new Map(),
    files: [],
    fileCount: 0
  }

  for (const folder of folders) {
    const segments = folder.relativePath.split('/').filter(Boolean)
    ensureNode(root, segments)
  }

  for (const file of files) {
    const segments = file.relativePath.split('/').filter(Boolean)
    const folderSegments = segments.slice(0, -1)
    const node =
      folderSegments.length === 0 ? root : ensureNode(root, folderSegments)
    node.files.push(file)
  }

  function countFiles(node) {
    let count = node.files.length
    for (const child of node.folders.values()) count += countFiles(child)
    node.fileCount = count
    return count
  }
  countFiles(root)

  return root
}

const ESTADO_KEYS_ORDEN = [
  'aprobado',
  'pendiente',
  'requiere-ajuste',
  'bloqueado',
  'obsoleto'
]

function emptyBreakdown() {
  return {
    aprobado: 0,
    pendiente: 0,
    'requiere-ajuste': 0,
    bloqueado: 0,
    obsoleto: 0,
    'sin-estado': 0
  }
}

function computeFolderStats(node) {
  let total = node.files.length
  const breakdown = emptyBreakdown()

  for (const f of node.files) {
    const entry = getDocumentEntry(f)
    const key = entry?.estado ? toCssKey(entry.estado) : 'sin-estado'
    if (breakdown[key] != null) breakdown[key] += 1
    else breakdown['sin-estado'] += 1
  }
  for (const child of node.folders.values()) {
    const cs = computeFolderStats(child)
    total += cs.total
    for (const k of Object.keys(breakdown)) breakdown[k] += cs.breakdown[k]
  }

  const conEstado = ESTADO_KEYS_ORDEN.reduce((sum, k) => sum + breakdown[k], 0)
  return { total, conEstado, breakdown }
}

const TOOLTIP_LABELS = {
  aprobado: ['aprobado', 'aprobados'],
  pendiente: ['pendiente', 'pendientes'],
  'requiere-ajuste': ['requiere ajuste', 'requieren ajuste'],
  bloqueado: ['bloqueado', 'bloqueados'],
  obsoleto: ['obsoleto', 'obsoletos'],
  'sin-estado': ['sin estado', 'sin estado']
}

function buildFolderTooltip(stats) {
  const lines = [
    `${stats.total} documento${stats.total === 1 ? '' : 's'} en total`
  ]
  const orden = [...ESTADO_KEYS_ORDEN, 'sin-estado']
  for (const key of orden) {
    const n = stats.breakdown[key]
    if (n > 0) {
      const [singular, plural] = TOOLTIP_LABELS[key]
      lines.push(`- ${n} ${n === 1 ? singular : plural}`)
    }
  }
  return lines.join('\n')
}

// ---------------------------------------------------------------------------
// Componentes auxiliares
// ---------------------------------------------------------------------------

const FILE_ICON = html`
  <svg
    class="tree__file-icon"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <polyline points="14 3 14 8 19 8" />
  </svg>
`

function StateDot({ estado }) {
  const key = toCssKey(estado) || 'sin-estado'
  const label = estado ? `Estado: ${toLabel(estado)}` : 'Sin estado'
  return html`<span
    class=${`tree__file-dot tree__file-dot--${key}`}
    aria-hidden="true"
    title=${label}
  ></span>`
}

function FolderCount({ stats }) {
  const tooltip = buildFolderTooltip(stats)

  if (stats.total === 0) {
    return html`<span class="tree__count" title="Sin documentos">0</span>`
  }

  if (stats.conEstado === 0) {
    return html`<span class="tree__count" title=${tooltip}>${stats.total}</span>`
  }

  return html`
    <span class="tree__count tree__count--with-progress" title=${tooltip}>
      <span class="tree__count-text">
        <strong class="tree__count-progress">${stats.conEstado}</strong>
        <span class="tree__count-divider">/</span>
        <span class="tree__count-total">${stats.total}</span>
      </span>
      <span class="tree__count-bar" aria-hidden="true">
        ${[...ESTADO_KEYS_ORDEN, 'sin-estado'].map((key) => {
          const n = stats.breakdown[key]
          if (n <= 0) return null
          return html`<span
            key=${key}
            class=${`tree__count-bar-seg tree__count-bar-seg--${key}`}
            style=${`flex-grow: ${n};`}
          ></span>`
        })}
      </span>
    </span>
  `
}

// ---------------------------------------------------------------------------
// TreeNode
// ---------------------------------------------------------------------------

function TreeNode({ node, depth, onSelectFile, selectedFileId }) {
  const hasChildren = node.folders.size > 0 || node.files.length > 0
  const [expanded, setExpanded] = useState(depth < 1)

  const childFolders = useMemo(
    () =>
      Array.from(node.folders.values()).sort((a, b) =>
        a.name.localeCompare(b.name, 'es')
      ),
    [node.folders]
  )
  const childFiles = useMemo(
    () => [...node.files].sort((a, b) => a.name.localeCompare(b.name, 'es')),
    [node.files]
  )

  const stats = computeFolderStats(node)

  return html`
    <li class="tree__node">
      <div class=${`tree__row tree__row--depth-${Math.min(depth, 5)}`}>
        ${hasChildren
          ? html`
              <button
                type="button"
                class="tree__toggle"
                aria-expanded=${expanded ? 'true' : 'false'}
                aria-label=${expanded ? 'Colapsar carpeta' : 'Expandir carpeta'}
                onClick=${() => setExpanded((v) => !v)}
              >${expanded ? '−' : '+'}</button>
            `
          : html`<span class="tree__toggle tree__toggle--leaf" aria-hidden="true">·</span>`}
        <span class="tree__name">${node.name}</span>
        <${FolderCount} stats=${stats} />
      </div>
      ${expanded
        ? html`
            <ul class="tree__children">
              ${childFolders.map(
                (child) => html`
                  <${TreeNode}
                    key=${child.path}
                    node=${child}
                    depth=${depth + 1}
                    onSelectFile=${onSelectFile}
                    selectedFileId=${selectedFileId}
                  />
                `
              )}
              ${childFiles.map((file) => {
                const entry = getDocumentEntry(file)
                const isSelected = selectedFileId === file.id
                return html`
                  <li class="tree__node" key=${file.id || file.relativePath}>
                    <button
                      type="button"
                      class=${`tree__file tree__file--depth-${Math.min(depth + 1, 6)} ${isSelected ? 'tree__file--selected' : ''}`}
                      onClick=${() => onSelectFile?.(file)}
                      title=${file.relativePath}
                    >
                      <${StateDot} estado=${entry?.estado} />
                      ${FILE_ICON}
                      <span class="tree__file-name">${file.name}</span>
                    </button>
                  </li>
                `
              })}
            </ul>
          `
        : null}
    </li>
  `
}

// ---------------------------------------------------------------------------
// HomeView
// ---------------------------------------------------------------------------

export function HomeView() {
  const [inv, setInv] = useState(getInventoryState())
  const [selectedFile, setSelectedFile] = useState(null)
  const [, forceRender] = useState(0)

  useEffect(() => {
    let cancelled = false

    const unsubInv = subscribeInventoryChange((next) => {
      if (!cancelled) setInv(next)
    })
    const unsubVal = subscribeValidation(() => {
      if (!cancelled) forceRender((v) => v + 1)
    })

    initInventory().catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[HomeView] initInventory falló:', err)
    })
    initValidation().catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[HomeView] initValidation falló:', err)
    })

    return () => {
      cancelled = true
      unsubInv()
      unsubVal()
    }
  }, [])

  const inventory = inv.data

  const tree = useMemo(() => {
    if (!inventory) return null
    return buildTree(inventory.folders, inventory.files)
  }, [inventory])

  const handleSelectFile = useCallback((file) => {
    setSelectedFile(file)
  }, [])

  const handleCloseDetail = useCallback(() => {
    setSelectedFile(null)
  }, [])

  if (inv.state === 'loading' || (inv.state === 'idle' && !inventory)) {
    return html`
      <div class="home-view">
        <section class="section">
          <h1 class="section__title">Inicio</h1>
          <p class="section__subtitle">Sincronizando inventario con Microsoft Graph…</p>
          <div class="notice">
            <strong>Cargando.</strong> Documentos escaneados:
            ${' '}<strong>${inv.progress.scanned}</strong>${inv.progress.queueSize != null
              ? html`<span> · Carpetas pendientes: <strong>${inv.progress.queueSize}</strong></span>`
              : null}.
          </div>
        </section>
      </div>
    `
  }

  if (inv.state === 'error' && !inventory) {
    return html`
      <div class="home-view">
        <section class="section">
          <h1 class="section__title">Inicio</h1>
          <p class="section__subtitle">No se pudo cargar el inventario desde SharePoint.</p>
          <div class="notice notice--alert">
            <strong>Error.</strong> ${inv.error}
            <br />
            Revisa la consola del navegador. Verifica permisos Graph y
            acceso al sitio "JMACyberSec".
          </div>
        </section>
      </div>
    `
  }

  const totalDocs = inventory.files.length
  const conMetadata = inventory.files.filter(
    (f) => f.metadata.codigoEntregable || f.metadata.fase || f.metadata.cliente
  ).length

  const decorated = inventory.files.map((f) => ({
    file: f,
    entry: getDocumentEntry(f)
  }))
  const conEstado = decorated.filter((d) => d.entry?.estado)
  const aprobados = conEstado.filter((d) => d.entry.estado === 'Aprobado').length
  const porcentajeAprobado =
    conEstado.length > 0 ? Math.round((aprobados / conEstado.length) * 100) : null

  return html`
    <div class="home-view">
      <section class="section">
        <h1 class="section__title">Inicio</h1>
        <p class="section__subtitle">
          Inventario sincronizado desde SharePoint del tenant JMA.
          Cliente piloto: Cliente_GWTC.
        </p>
      </section>

      <section class="section">
        <h2>Indicadores generales</h2>
        <div class="kpi-grid" style="margin-top: var(--space-4);">
          <article class="kpi-card">
            <div class="kpi-card__label">Total de documentos</div>
            <div class="kpi-card__value">${totalDocs}</div>
            <div class="kpi-card__hint">.docx, .xlsx, .pptx, .pdf</div>
          </article>
          <article class="kpi-card">
            <div class="kpi-card__label">Con metadata identificada</div>
            <div class="kpi-card__value">${conMetadata}</div>
            <div class="kpi-card__hint">Fase, entregable o cliente</div>
          </article>
          <article class="kpi-card">
            <div class="kpi-card__label">Con estado registrado</div>
            <div class="kpi-card__value">${conEstado.length}</div>
            <div class="kpi-card__hint">Validados al menos una vez</div>
          </article>
          <article class="kpi-card">
            <div class="kpi-card__label">% Aprobados</div>
            <div class="kpi-card__value">${porcentajeAprobado == null ? '—' : `${porcentajeAprobado}%`}</div>
            <div class="kpi-card__hint">Sobre los documentos con estado</div>
          </article>
        </div>
      </section>

      <section class="section">
        <h2>Estructura del proyecto</h2>
        <p class="section__subtitle">
          Carpetas con conteo de aprobados / total. Clic en un archivo
          para abrir su detalle y registrar validación.
        </p>
        <ul class="tree">
          <${TreeNode}
            node=${tree}
            depth=${0}
            onSelectFile=${handleSelectFile}
            selectedFileId=${selectedFile?.id}
          />
        </ul>
      </section>

      ${selectedFile
        ? html`<${DocumentDetail} file=${selectedFile} onClose=${handleCloseDetail} />`
        : null}
    </div>
  `
}
